/* do not delete this comment
registration datetime rules
please put 
registration_start_at value same as published_at value
registration_end_at value same as activity_start_at value
*/

import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zhTW } from "date-fns/locale";
import Datepicker from "../Utilities/Datepicker/Datepicker";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../Utilities/Button";
import Switcher from "../Utilities/AlertSwitch/Switcher";
import TextField from "../Utilities/TextField";
import Toast from "../Utilities/Toast";
import RightPanelLayout from "../Layout/RightPanelLayout";
import styles from "./style/EventFormPage.module.scss";
import Alert from "../Utilities/Alert";
import Modal from "../Utilities/Modal";
import {
  createActivity,
  deleteActivity,
  fetchActivityById,
  updateActivity,
} from "./activitiesApi";
import {
  buildActivityPayload,
  getActivitySaveErrorToast,
  getPublishMissingFields,
  mapActivityDetailToFormValues,
} from "./activityTransforms";
import { computeActivityStatus } from "./activityStatus";
import { MdError, MdKeyboardArrowLeft } from "react-icons/md";
import EventStatusBadge from "./EventStatusBadge";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import EventRichTextEditor from "./EventRichTextEditor";
import SplitHourMinuteTimeInput from "./SplitHourMinuteTimeInput";
import TagColorPicker from "./TagColorPicker";
import RegistrationCountCell from "./RegistrationCountCell";
import { GoClock, GoPeople } from "react-icons/go";
import { CgScreen } from "react-icons/cg";
import { IoMdOpen } from "react-icons/io";

const EventFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activityDetail, setActivityDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(mode === "edit");

  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);

  const nowLocalForInput = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const toLocalDatetimeInput = (value) => {
    if (!value) return "";
    if (value.includes("T")) return value.slice(0, 16);
    const parts = value.split(" ");
    if (parts.length !== 2) return "";
    const [datePart, timePartRaw] = parts;
    const timePart = (timePartRaw || "").slice(0, 5);
    return `${datePart}T${timePart}`;
  };

  const initialForm = useMemo(
    () => ({
      title: "",
      subtitle: "",
      summary: "",
      content: "",
      location: "",
      targetAudience: "",
      tags: [],

      capacity: "",
      almostFullThreshold: "",
      reservedCount: "",

      activityStartAt: "",
      activityEndAt: "",
      registrationStartAt: "",
      registrationEndAt: "",

      isPublished: false,
      publishedAt: "",
      unpublishedAt: "",

      isPinned: false,
      pinnedAt: "",
      sortOrder: "",
    }),
    [],
  );

  const {
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: initialForm,
  });
  const form = watch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [toolbarSaveWarning, setToolbarSaveWarning] = useState("");
  const [publishSwitchWarning, setPublishSwitchWarning] = useState("");
  const [activitySettingWarnings, setActivitySettingWarnings] = useState([]);
  const [tagDraftName, setTagDraftName] = useState("");
  const [tagDraftColor, setTagDraftColor] = useState({
    bg: "#FFE4E4",
    text: "#FE7F7F",
    theme: "red",
  });
  const [previewVisibleTagCount, setPreviewVisibleTagCount] = useState(null);
  const previewTagListRef = useRef(null);
  const previewMeasureMoreRef = useRef(null);
  const previewMeasureTagRefs = useRef([]);

  const resolveActivityDate = (prev) => {
    if (prev.activityStartAt && prev.activityStartAt.length >= 10) {
      return prev.activityStartAt.slice(0, 10);
    }
    if (prev.activityEndAt && prev.activityEndAt.length >= 10) {
      return prev.activityEndAt.slice(0, 10);
    }
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleActivityDatePick = (date) => {
    const prev = getValues();
    if (!date) {
      setValue("activityStartAt", "", { shouldDirty: true });
      setValue("activityEndAt", "", { shouldDirty: true });
      return;
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const startT =
      prev.activityStartAt && prev.activityStartAt.length >= 16
        ? prev.activityStartAt.slice(11, 16)
        : "09:00";
    const endT =
      prev.activityEndAt && prev.activityEndAt.length >= 16
        ? prev.activityEndAt.slice(11, 16)
        : "17:00";
    setValue("activityStartAt", `${dateStr}T${startT}`, { shouldDirty: true });
    setValue("activityEndAt", `${dateStr}T${endT}`, { shouldDirty: true });
  };

  const onActivityStartTimeCommit = (hhmm) => {
    const t = hhmm || "00:00";
    const prev = getValues();
    const date = resolveActivityDate(prev);
    setValue("activityStartAt", `${date}T${t}`, { shouldDirty: true });
  };

  const onActivityEndTimeCommit = (hhmm) => {
    const t = hhmm || "00:00";
    const prev = getValues();
    const date = resolveActivityDate(prev);
    setValue("activityEndAt", `${date}T${t}`, { shouldDirty: true });
  };

  const activityDatePart =
    form.activityStartAt && form.activityStartAt.length >= 10
      ? form.activityStartAt.slice(0, 10)
      : form.activityEndAt && form.activityEndAt.length >= 10
        ? form.activityEndAt.slice(0, 10)
        : "";
  const activityStartTimePart =
    form.activityStartAt && form.activityStartAt.length >= 16
      ? form.activityStartAt.slice(11, 16)
      : "";
  const activityEndTimePart =
    form.activityEndAt && form.activityEndAt.length >= 16
      ? form.activityEndAt.slice(11, 16)
      : "";

  const activityDateForPicker = useMemo(() => {
    if (!activityDatePart) return "";
    const d = new Date(`${activityDatePart}T12:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d;
  }, [activityDatePart]);

  const publishedDatePart =
    form.publishedAt && form.publishedAt.length >= 10
      ? form.publishedAt.slice(0, 10)
      : "";
  const publishedTimePart =
    form.publishedAt && form.publishedAt.length >= 16
      ? form.publishedAt.slice(11, 16)
      : "";

  const publishedDateForPicker = useMemo(() => {
    if (!publishedDatePart) return "";
    const d = new Date(`${publishedDatePart}T12:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d;
  }, [publishedDatePart]);

  const handlePublishedDatePick = (date) => {
    const prev = getValues();
    if (!date) {
      setValue("publishedAt", "", { shouldDirty: true });
      return;
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const t =
      prev.publishedAt && prev.publishedAt.length >= 16
        ? prev.publishedAt.slice(11, 16)
        : "10:00";
    setValue("publishedAt", `${yyyy}-${mm}-${dd}T${t}`, { shouldDirty: true });
  };

  const onPublishedTimeCommit = (hhmm) => {
    const t = hhmm || "00:00";
    const prev = getValues();
    const date =
      prev.publishedAt && prev.publishedAt.length >= 10
        ? prev.publishedAt.slice(0, 10)
        : nowLocalForInput().slice(0, 10);
    setValue("publishedAt", `${date}T${t}`, { shouldDirty: true });
  };

  const unpublishedDatePart =
    form.unpublishedAt && form.unpublishedAt.length >= 10
      ? form.unpublishedAt.slice(0, 10)
      : "";
  const unpublishedTimePart =
    form.unpublishedAt && form.unpublishedAt.length >= 16
      ? form.unpublishedAt.slice(11, 16)
      : "";

  const unpublishedDateForPicker = useMemo(() => {
    if (!unpublishedDatePart) return "";
    const d = new Date(`${unpublishedDatePart}T12:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d;
  }, [unpublishedDatePart]);

  const handleUnpublishedDatePick = (date) => {
    const prev = getValues();
    if (!date) {
      setValue("unpublishedAt", "", { shouldDirty: true });
      return;
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const t =
      prev.unpublishedAt && prev.unpublishedAt.length >= 16
        ? prev.unpublishedAt.slice(11, 16)
        : "10:00";
    setValue("unpublishedAt", `${yyyy}-${mm}-${dd}T${t}`, { shouldDirty: true });
  };

  const onUnpublishedTimeCommit = (hhmm) => {
    const t = hhmm || "00:00";
    const prev = getValues();
    const date =
      prev.unpublishedAt && prev.unpublishedAt.length >= 10
        ? prev.unpublishedAt.slice(0, 10)
        : nowLocalForInput().slice(0, 10);
    setValue("unpublishedAt", `${date}T${t}`, { shouldDirty: true });
  };

  const capacityIntForStats = useMemo(() => {
    const n = Number.parseInt(String(form.capacity).trim(), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [form.capacity]);

  const registrationStats = useMemo(() => {
    const registered =
      mode === "edit" && activityDetail != null
        ? Number(
            activityDetail.registration_count ??
              activityDetail.reserved_count ??
              0,
          )
        : 0;
    const cap =
      capacityIntForStats != null && capacityIntForStats > 0
        ? capacityIntForStats
        : mode === "edit" &&
            activityDetail?.capacity != null &&
            Number(activityDetail.capacity) > 0
          ? Number(activityDetail.capacity)
          : null;
    return { registered, capacity: cap };
  }, [activityDetail, capacityIntForStats, mode]);

  const appendTag = () => {
    const name = tagDraftName.trim();
    if (!name) return;
    const prevTags = getValues("tags") || [];
    setValue(
      "tags",
      [
        ...prevTags,
        {
          id: `tag-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          name,
          color: {
            bg: tagDraftColor.bg,
            text: tagDraftColor.text,
            theme: tagDraftColor.theme || "red",
          },
        },
      ],
      { shouldDirty: true },
    );
    setTagDraftName("");
  };

  const removeTag = (tagId) => {
    const prevTags = getValues("tags") || [];
    setValue(
      "tags",
      prevTags.filter((t) => t.id !== tagId),
      { shouldDirty: true },
    );
  };

  useEffect(() => {
    if (mode !== "edit" || !id) {
      setDetailLoading(false);
      setActivityDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    fetchActivityById(id)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setActivityDetail(data);
        reset(
          mapActivityDetailToFormValues(data, { toLocalDatetimeInput }),
        );
        clearErrors();
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setActivityDetail(null);
        navigate("/dashboard/tdp/events");
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mode, id, reset, clearErrors, navigate]);

  useEffect(() => {
    if (mode === "new") {
      reset(initialForm);
      clearErrors();
    }
  }, [mode, initialForm, reset, clearErrors]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setPendingNav("/dashboard/tdp/events");
      setIsConfirmLeaveOpen(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirty]);

  const setField = (key, value) => {
    setValue(key, value, { shouldDirty: true });
    if (value !== "" && value != null) {
      clearErrors(key);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    if (!isDirty) {
      navigate("/dashboard/tdp/events");
      return;
    }
    setPendingNav("/dashboard/tdp/events");
    setIsConfirmLeaveOpen(true);
  };

  const computedFormStatus = useMemo(
    () =>
      computeActivityStatus({
        isPublished: form.isPublished,
        activityStartAt: form.activityStartAt,
        publishedAt: form.publishedAt,
        registered: registrationStats.registered,
        capacity: registrationStats.capacity,
      }),
    [
      form.activityStartAt,
      form.isPublished,
      form.publishedAt,
      registrationStats.capacity,
      registrationStats.registered,
    ],
  );

  const publishSegmentText = computedFormStatus.text;

  const handlePreview = () => {
    setIsPreviewOpen(true);
  };

  const previewSummaryText = useMemo(() => {
    if (form.summary?.trim()) return form.summary.trim();
    const plain = String(form.content || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return plain || "請輸入活動摘要或內文介紹";
  }, [form.content, form.summary]);

  const previewDateTimeText = useMemo(() => {
    const start = form.activityStartAt ? new Date(form.activityStartAt) : null;
    const end = form.activityEndAt ? new Date(form.activityEndAt) : null;
    if (
      !start ||
      Number.isNaN(start.getTime()) ||
      !end ||
      Number.isNaN(end.getTime())
    ) {
      return "請設定活動時間";
    }
    const weekdayText = ["日", "一", "二", "三", "四", "五", "六"][
      start.getDay()
    ];
    const yyyy = start.getFullYear();
    const mm = String(start.getMonth() + 1).padStart(2, "0");
    const dd = String(start.getDate()).padStart(2, "0");
    const sh = String(start.getHours()).padStart(2, "0");
    const sm = String(start.getMinutes()).padStart(2, "0");
    const eh = String(end.getHours()).padStart(2, "0");
    const em = String(end.getMinutes()).padStart(2, "0");
    return `${yyyy} / ${mm} / ${dd}（${weekdayText}）${sh}:${sm}-${eh}:${em}`;
  }, [form.activityEndAt, form.activityStartAt]);

  useEffect(() => {
    if (!isPreviewOpen) return undefined;
    if (!form.tags.length) {
      setPreviewVisibleTagCount(0);
      return undefined;
    }

    const container = previewTagListRef.current;
    if (!container) return undefined;

    const gap = 10;
    const computeVisibleTagCount = () => {
      const measureEls = previewMeasureTagRefs.current;
      const widths = form.tags.map((_, idx) => measureEls[idx]?.offsetWidth || 0);
      if (!widths.length || widths.some((w) => w <= 0)) {
        setPreviewVisibleTagCount(form.tags.length);
        return;
      }

      const getMoreWidth = (count) => {
        const moreEl = previewMeasureMoreRef.current;
        if (!moreEl) return 34;
        moreEl.textContent = `+${count}`;
        return moreEl.offsetWidth || 34;
      };

      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) {
        setPreviewVisibleTagCount(form.tags.length);
        return;
      }

      const allTagsWidth = widths.reduce(
        (sum, width, idx) => sum + width + (idx > 0 ? gap : 0),
        0,
      );
      if (allTagsWidth <= containerWidth) {
        setPreviewVisibleTagCount(form.tags.length);
        return;
      }

      let bestCount = 0;
      for (let visible = form.tags.length - 1; visible >= 0; visible -= 1) {
        const hidden = form.tags.length - visible;
        const visibleWidth = widths
          .slice(0, visible)
          .reduce((sum, width, idx) => sum + width + (idx > 0 ? gap : 0), 0);
        const moreWidth =
          (visible > 0 ? gap : 0) + (hidden > 0 ? getMoreWidth(hidden) : 0);
        if (visibleWidth + moreWidth <= containerWidth) {
          bestCount = visible;
          break;
        }
      }
      setPreviewVisibleTagCount(bestCount);
    };

    const raf = window.requestAnimationFrame(computeVisibleTagCount);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(computeVisibleTagCount)
        : null;
    if (resizeObserver) {
      resizeObserver.observe(container);
    }

    return () => {
      window.cancelAnimationFrame(raf);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [form.tags, isPreviewOpen]);

  const visiblePreviewTags = useMemo(() => {
    if (!form.tags.length) return [];
    if (previewVisibleTagCount == null) return form.tags;
    return form.tags.slice(0, previewVisibleTagCount);
  }, [form.tags, previewVisibleTagCount]);

  const hiddenPreviewTagCount = Math.max(
    0,
    form.tags.length - visiblePreviewTags.length,
  );

  const getActivitySettingWarnings = (values) => {
    const warnings = [];
    const publishedAt = values.publishedAt ? new Date(values.publishedAt) : null;
    const activityStartAt = values.activityStartAt
      ? new Date(values.activityStartAt)
      : null;
    const unpublishedAt = values.unpublishedAt
      ? new Date(values.unpublishedAt)
      : null;

    const hasPublishedAt =
      publishedAt && !Number.isNaN(publishedAt.getTime());
    const hasActivityStartAt =
      activityStartAt && !Number.isNaN(activityStartAt.getTime());
    const hasUnpublishedAt =
      unpublishedAt && !Number.isNaN(unpublishedAt.getTime());

    if (hasPublishedAt && hasActivityStartAt && publishedAt > activityStartAt) {
      warnings.push("上架開始時間不可晚於活動開始時間。");
    }

    if (hasPublishedAt && hasUnpublishedAt && publishedAt > unpublishedAt) {
      warnings.push("上架開始時間不可晚於下架時間。");
    }

    if (
      hasUnpublishedAt &&
      hasActivityStartAt &&
      unpublishedAt > activityStartAt
    ) {
      warnings.push("下架時間不可晚於活動開始時間。");
    }

    return warnings;
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    clearErrors();
    setToolbarSaveWarning("");
    setPublishSwitchWarning("");
    setActivitySettingWarnings([]);

    if (!form.isPublished) {
      if (!form.title.trim()) {
        setError("title", { type: "manual", message: "此欄位為必填" });
        setToolbarSaveWarning("未輸入標題");
        return;
      }
    } else {
      const missing = getPublishMissingFields(form);

      missing.forEach((field) => {
        setError(field, { type: "manual", message: "此欄位為必填" });
      });

      if (missing.length > 0) {
        setPublishSwitchWarning("尚有未完成的內容");
        return;
      }
    }

    const submitTimeWarnings = getActivitySettingWarnings(form);
    if (submitTimeWarnings.length > 0) {
      setActivitySettingWarnings([submitTimeWarnings[0]]);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildActivityPayload(form);
      if (mode === "new") {
        await createActivity(payload);
      } else {
        await updateActivity(id, payload);
      }
      reset(form);
      setToastState({
        show: true,
        title: form.isPublished
          ? `已上架活動「${form.title.trim()}」。`
          : `已儲存活動「${form.title.trim()}」。`,
        subtitle: "",
        error: false,
      });
      window.setTimeout(() => {
        setToastState((s) => ({ ...s, show: false }));
        navigate("/dashboard/tdp/events");
      }, 1400);
    } catch (err) {
      const toast = getActivitySaveErrorToast(err);
      setToastState({
        show: true,
        title: toast.title,
        subtitle: "",
        error: toast.error,
      });
      window.setTimeout(() => {
        setToastState((s) => ({ ...s, show: false }));
      }, 3200);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === "edit" && id && detailLoading) {
    return (
      <RightPanelLayout title="活動管理">
        <div className={styles.emptyMessage}>載入中…</div>
      </RightPanelLayout>
    );
  }

  return (
    <RightPanelLayout title="活動管理">
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className={styles.topToolbarHeader}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleCancel}
          >
            <MdKeyboardArrowLeft size={32} />
            返回
          </button>

          <div className={styles.rightActions}>
            {toolbarSaveWarning ? (
              <span className={styles.toolbarWarningText}>
                <MdError size={22} aria-hidden />
                {toolbarSaveWarning}
              </span>
            ) : null}
            <EventStatusBadge
              theme={computedFormStatus.theme}
              text={publishSegmentText}
            />

            <button
              type="button"
              className={styles.previewButton}
              onClick={handlePreview}
            >
              預覽
            </button>

            <Button
              type="primary"
              title={isSubmitting ? "儲存中…" : "儲存"}
              disabled={isSubmitting}
            />

            {mode === "edit" && activityDetail ? (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => setIsDeleteOpen(true)}
                aria-label="刪除活動"
              >
                <FiTrash2 size={24} aria-hidden />
                刪除活動
              </button>
            ) : null}
          </div>
        </div>

        <div className={styles.formSheet}>
          <div className={styles.editColumns}>
            <div className={styles.editColumn}>
              <div className={styles.activityInfoColumn}>
                <div className={styles.columnStack}>
                  <div
                    className={`${styles.editableBlock} ${styles.editableBlockTitleOnly}`}
                  >
                    <div className={styles.titleFieldRow}>
                      <div className={styles.activityTitleLabel}>
                        活動標題
                        <span className={styles.requiredPrimary}>*</span>
                      </div>
                      <div className={styles.titleFieldInputWrap}>
                        <TextField
                          placeholder="請輸入"
                          width="100%"
                          fullWidth
                          value={form.title}
                          error={Boolean(errors.title)}
                          onChange={(v) => setField("title", v)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.sectionTitle}>活動資訊</div>

                  <div className={styles.editableBlock}>
                    <div
                      className={`${styles.infoRow} ${styles.activityTimeInfoRow}`}
                    >
                      <div className={styles.infoRowLabel}>活動時間</div>
                      <div className={styles.infoRowBody}>
                        <div className={styles.activityTimeControls}>
                          <div className={styles.activityDatePickerWrap}>
                            <Datepicker
                              selectDate={activityDateForPicker}
                              setSelectDate={handleActivityDatePick}
                              error={Boolean(
                                errors.activityStartAt || errors.activityEndAt,
                              )}
                              iconType="arrow"
                              placeholderText="請選擇日期"
                              dateFormat="yyyy-MM-dd"
                              locale={zhTW}
                            />
                          </div>
                          <SplitHourMinuteTimeInput
                            value={activityStartTimePart}
                            onCommit={onActivityStartTimeCommit}
                            ariaLabel="活動開始時間"
                          />
                          <span className={styles.infoTimeSep}>-</span>
                          <SplitHourMinuteTimeInput
                            value={activityEndTimePart}
                            onCommit={onActivityEndTimeCommit}
                            ariaLabel="活動結束時間"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.editableBlock}>
                    <div className={styles.infoRow}>
                      <div className={styles.infoRowLabel}>適合對象</div>
                      <div className={styles.infoRowBody}>
                        <TextField
                          placeholder="請輸入"
                          width="100%"
                          fullWidth
                          value={form.targetAudience}
                          error={Boolean(errors.targetAudience)}
                          onChange={(v) => setField("targetAudience", v)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.editableBlock}>
                    <div className={styles.infoRow}>
                      <div className={styles.infoRowLabel}>活動地點</div>
                      <div className={styles.infoRowBody}>
                        <TextField
                          placeholder="請貼上網址"
                          width="100%"
                          fullWidth
                          value={form.location}
                          error={Boolean(errors.location)}
                          onChange={(v) => setField("location", v)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.editableBlock}>
                    <div
                      className={`${styles.infoRow} ${styles.infoRowAlignStart}`}
                    >
                      <div className={styles.infoRowLabel}>活動標籤</div>
                      <div className={styles.infoRowBody}>
                        {form.tags.length > 0 ? (
                          <div className={styles.tagList}>
                            {form.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className={styles.tagChip}
                                style={{
                                  borderColor: tag.color.bg,
                                  backgroundColor: tag.color.bg,
                                  color: tag.color.text,
                                }}
                              >
                                {tag.name}
                                <button
                                  type="button"
                                  className={styles.tagChipRemove}
                                  onClick={() => removeTag(tag.id)}
                                  aria-label={`移除標籤 ${tag.name}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className={styles.tagHint}>請由下方新增標籤</p>
                        )}
                        <div className={styles.tagAddRow}>
                          <div className={styles.tagNameField}>
                            <TextField
                              placeholder="請輸入標籤名稱"
                              width="100%"
                              fullWidth
                              value={tagDraftName}
                              onChange={(v) => setTagDraftName(v)}
                            />
                          </div>
                          <TagColorPicker
                            value={tagDraftColor}
                            onChange={setTagDraftColor}
                          />
                          <button
                            type="button"
                            className={styles.tagAddButton}
                            onClick={appendTag}
                          >
                            <FiPlus size={18} aria-hidden />
                            新增
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.editableBlock}>
                    <div
                      className={`${styles.infoRow} ${styles.infoRowAlignStart}`}
                    >
                      <div className={styles.infoRowLabel}>內文介紹</div>
                      <div className={styles.infoRowBody}>
                        <div
                          className={
                            errors.content
                              ? styles.richTextErrorWrap
                              : undefined
                          }
                        >
                          <EventRichTextEditor
                            value={form.content}
                            onChange={(html) => setField("content", html)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={styles.columnDivider}
              role="separator"
              aria-orientation="vertical"
            />
            <div className={styles.editColumn}>
              <div className={styles.rightFormColumn}>
                <div className={styles.columnStack}>
                  <div className={styles.sectionTitle}>報名狀況</div>
                  <div className={styles.editableBlock}>
                    <div className={styles.registrationStatBlock}>
                      <div className={styles.infoRowLabel}>已報名/人數上限</div>
                      <RegistrationCountCell
                        registered={registrationStats.registered}
                        capacity={registrationStats.capacity}
                        width="100%"
                        maxWidth="337px"
                        gap="10px"
                      />
                    </div>
                  </div>
                  <div className={styles.sectionTitleWithWarning}>
                    <div className={styles.sectionTitle}>活動設定</div>
                    {activitySettingWarnings.length > 0 ? (
                      <div className={styles.sectionWarningList}>
                        {activitySettingWarnings.map((warning) => (
                          <span
                            key={warning}
                            className={styles.sectionWarningItem}
                          >
                            <MdError size={16} aria-hidden />
                            {warning}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`gap-2 flex flex-col ${styles.editableBlock}`}
                  >
                    <div className={styles.infoRow}>
                      <div className={styles.infoRowLabel}>上架</div>
                      <div className={styles.infoRowBody}>
                        <div className={styles.switchRow}>
                          <Switcher
                            isChecked={form.isPublished}
                            onChange={() => {
                              setPublishSwitchWarning("");
                              setField("isPublished", !form.isPublished);
                            }}
                            color={{ on: "#5856D6", off: "#5856D64D" }}
                            width={33}
                            height={20}
                          />
                          {publishSwitchWarning ? (
                            <span className={styles.publishInlineWarning}>
                              <MdError size={22} aria-hidden />{" "}
                              {publishSwitchWarning}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoRow}>
                      <div className={styles.infoRowLabel}>設定上架時間</div>
                    </div>
                    <div className={`${styles.infoRow} !gap-2`}>
                      <div className={`${styles.infoRowLabel} !min-w-fit`}>
                        上架時間
                      </div>
                      <div className={styles.infoRowBody}>
                        <div className={styles.activityTimeControls}>
                          <div className={styles.activityDatePickerWrap}>
                            <Datepicker
                              selectDate={publishedDateForPicker}
                              setSelectDate={handlePublishedDatePick}
                              error={Boolean(errors.publishedAt)}
                              iconType="arrow"
                              placeholderText="選擇日期"
                              dateFormat="yyyy-MM-dd"
                              locale={zhTW}
                            />
                          </div>
                          <SplitHourMinuteTimeInput
                            value={publishedTimePart || "10:00"}
                            onCommit={onPublishedTimeCommit}
                            ariaLabel="上架開始時間"
                          />
                          <span className={styles.infoTimeSep}>-</span>
                          <div className={styles.activityDatePickerWrap}>
                            <Datepicker
                              selectDate={unpublishedDateForPicker}
                              setSelectDate={handleUnpublishedDatePick}
                              error={Boolean(errors.unpublishedAt)}
                              iconType="arrow"
                              placeholderText="選擇日期"
                              dateFormat="yyyy-MM-dd"
                              locale={zhTW}
                            />
                          </div>
                          <SplitHourMinuteTimeInput
                            value={unpublishedTimePart || "10:00"}
                            onCommit={onUnpublishedTimeCommit}
                            ariaLabel="上架結束時間"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.editableBlock}>
                    <div className={styles.infoRow}>
                      <div className={styles.infoRowLabel}>報名人數上限</div>
                      <div className={styles.infoRowBody}>
                        <TextField
                          placeholder="請輸入人數"
                          width="100%"
                          fullWidth
                          value={form.capacity}
                          error={Boolean(errors.capacity)}
                          onChange={(v) =>
                            setField("capacity", String(v).replace(/\D/g, ""))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <Modal
        isOpen={isPreviewOpen}
        size="xl"
        width="min(460px, calc(100vw - 32px))"
      >
        <div className={styles.previewModalRoot}>
          <header className={styles.previewModalHeader}>
            <h2 className={styles.previewModalTitle}>預覽</h2>
          </header>

          <div className={styles.previewModalBody}>
            <article className={styles.previewCard}>
              <div className={styles.previewCardTagRow}>
                <div className={styles.previewCardTagList} ref={previewTagListRef}>
                  {form.tags.length > 0 ? (
                    <>
                      {visiblePreviewTags.map((tag) => (
                        <span
                          key={tag.id}
                          className={styles.previewTag}
                          style={{
                            backgroundColor: tag.color.bg,
                            color: tag.color.text,
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                      {hiddenPreviewTagCount > 0 ? (
                        <span className={styles.previewTagMore}>
                          +{hiddenPreviewTagCount}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className={styles.previewTagPlaceholder}>
                      未設定標籤
                    </span>
                  )}
                </div>
                <div className={styles.previewCardStatus}>
                  <EventStatusBadge
                    theme={computedFormStatus.theme}
                    text={publishSegmentText}
                  />
                  <div className={styles.previewCardCheckboxHolder} />
                </div>
              </div>

              <h3 className={styles.previewCardTitle}>
                {form.title.trim() || "未命名活動"}
              </h3>

              <p className={styles.previewCardSummary}>{previewSummaryText}</p>

              <button type="button" className={styles.previewViewMoreButton}>
                <span>查看更多</span>
                <IoMdOpen size={16} aria-hidden />
              </button>

              <div className={styles.previewInfoBlock}>
                <div className={styles.previewInfoTitle}>活動資訊</div>
                <div className={styles.previewMetaList}>
                  <div className={styles.previewMetaItem}>
                    <GoPeople size={14} aria-hidden />
                    <span>{form.targetAudience.trim() || "請設定適合對象"}</span>
                  </div>
                  <div className={styles.previewMetaItem}>
                    <GoClock size={14} aria-hidden />
                    <span>{previewDateTimeText}</span>
                  </div>
                  <div className={styles.previewMetaItem}>
                    <CgScreen size={14} aria-hidden />
                    <span>{form.location.trim() || "請設定活動地點"}</span>
                  </div>
                </div>
              </div>

              <div className={styles.previewTagMeasure} aria-hidden>
                {form.tags.map((tag, idx) => (
                  <span
                    key={tag.id}
                    ref={(el) => {
                      previewMeasureTagRefs.current[idx] = el;
                    }}
                    className={styles.previewTagMeasureItem}
                  >
                    {tag.name}
                  </span>
                ))}
                <span
                  ref={previewMeasureMoreRef}
                  className={styles.previewTagMoreMeasure}
                >
                  +0
                </span>
              </div>
            </article>
          </div>

          <footer className={styles.previewModalFooter}>
            <button
              type="button"
              className={styles.previewModalCloseButton}
              onClick={() => setIsPreviewOpen(false)}
            >
              關閉
            </button>
          </footer>
        </div>
      </Modal>

      <Alert
        isOpen={isConfirmLeaveOpen}
        onClose={() => {
          setIsConfirmLeaveOpen(false);
          setPendingNav(null);
        }}
        onConfirm={() => {
          setIsConfirmLeaveOpen(false);
          const next = pendingNav;
          setPendingNav(null);
          if (next) navigate(next);
        }}
        leftButtonText="留在此頁"
        rightButtonText="離開"
        mainTitle="尚未儲存，確定離開？"
        subTitle="離開後本次填寫的內容將不會保留。"
        isWarning
      />

      <Alert
        isOpen={mode === "edit" && Boolean(activityDetail) && isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={async () => {
          setIsDeleteOpen(false);
          try {
            await deleteActivity(id);
            setToastState({
              show: true,
              title: "已刪除活動",
              subtitle: activityDetail
                ? `「${activityDetail.title ?? activityDetail.name}」`
                : "",
              error: false,
            });
            window.setTimeout(() => {
              setToastState((s) => ({ ...s, show: false }));
              navigate("/dashboard/tdp/events");
            }, 900);
          } catch {
          }
        }}
        leftButtonText="取消"
        rightButtonText="刪除活動"
        mainTitle="確認刪除活動？"
        subTitle={
          activityDetail
            ? `「${activityDetail.title ?? activityDetail.name}」將被刪除。`
            : ""
        }
        isWarning
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default EventFormPage;
